/**
 * Word (.docx) download for the PAYE HMRC claim letter.
 * OOXML is assembled here so we do not take on a document library for one file.
 * The package is built for Microsoft Word and Apple Pages: Pages ignores
 * document defaults, draws `nil` table borders as a grid, and stretches
 * paragraph borders across the page, so fonts, a Normal style, `none`
 * borders and a short signature table are set explicitly.
 */

import { downloadBlob } from '@/helpers/downloadBlob'
import { buildStoredZip } from '@/helpers/zipStore'
import {
  claimLetterFilename,
  type ClaimLetterContent,
} from '@/helpers/claimTaxBack/claimLetter'

const INK = '002D43'
const MUTED = '5A7280'
const RULE = '9BB0BA'
const HEADER_FILL = 'F4F8FA'
const FONT = 'Times New Roman'
/** A4 content width in twips: 11906 page minus 1440 left and right margins. */
const CONTENT_WIDTH = 9026
const SIGNATURE_WIDTH = 2400

function xmlEscape(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function rFonts(): string {
  return `<w:rFonts w:ascii="${FONT}" w:hAnsi="${FONT}" w:cs="${FONT}" w:eastAsia="${FONT}"/>`
}

type ParagraphOpts = {
  bold?: boolean
  italics?: boolean
  after?: number
  before?: number
  align?: 'left' | 'right'
  size?: number
  color?: string
  line?: number
}

function runProperties(opts: {
  bold?: boolean
  italics?: boolean
  size?: number
  color?: string
}): string {
  const parts = [rFonts()]
  if (opts.bold) parts.push('<w:b/><w:bCs/>')
  if (opts.italics) parts.push('<w:i/><w:iCs/>')
  const size = opts.size ?? 22
  parts.push(`<w:sz w:val="${size}"/>`)
  parts.push(`<w:szCs w:val="${size}"/>`)
  parts.push(`<w:color w:val="${opts.color ?? INK}"/>`)
  parts.push('<w:lang w:val="en-GB"/>')
  return `<w:rPr>${parts.join('')}</w:rPr>`
}

function paragraph(text: string, opts: ParagraphOpts = {}): string {
  const pPrParts = ['<w:pStyle w:val="Normal"/>']
  const spacingAttrs: string[] = []
  if (opts.after != null) spacingAttrs.push(`w:after="${opts.after}"`)
  if (opts.before != null) spacingAttrs.push(`w:before="${opts.before}"`)
  spacingAttrs.push(`w:line="${opts.line ?? 276}"`)
  spacingAttrs.push('w:lineRule="auto"')
  pPrParts.push(`<w:spacing ${spacingAttrs.join(' ')}/>`)
  if (opts.align) pPrParts.push(`<w:jc w:val="${opts.align}"/>`)

  return `<w:p><w:pPr>${pPrParts.join('')}</w:pPr><w:r>${runProperties({
    bold: opts.bold,
    italics: opts.italics,
    size: opts.size,
    color: opts.color,
  })}<w:t xml:space="preserve">${xmlEscape(text)}</w:t></w:r></w:p>`
}

function noneBorder(side: 'top' | 'left' | 'bottom' | 'right' | 'insideH' | 'insideV'): string {
  return `<w:${side} w:val="none" w:sz="0" w:space="0" w:color="auto"/>`
}

function lineBorder(
  side: 'top' | 'left' | 'bottom' | 'right' | 'insideH' | 'insideV',
  color: string,
  size = 8
): string {
  return `<w:${side} w:val="single" w:sz="${size}" w:space="0" w:color="${color}"/>`
}

/** Short ruled line for the signature. A paragraph border becomes full-width in Pages. */
function signatureLine(): string {
  return `<w:tbl><w:tblPr><w:tblStyle w:val="TableNormal"/><w:tblW w:w="${SIGNATURE_WIDTH}" w:type="dxa"/><w:tblLayout w:type="fixed"/><w:tblBorders>${lineBorder('bottom', RULE, 6)}${noneBorder('top')}${noneBorder('left')}${noneBorder('right')}${noneBorder('insideH')}${noneBorder('insideV')}</w:tblBorders></w:tblPr><w:tblGrid><w:gridCol w:w="${SIGNATURE_WIDTH}"/></w:tblGrid><w:tr><w:tc><w:tcPr><w:tcW w:w="${SIGNATURE_WIDTH}" w:type="dxa"/><w:tcMar><w:top w:w="0" w:type="dxa"/><w:left w:w="0" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="0" w:type="dxa"/></w:tcMar></w:tcPr><w:p><w:pPr><w:pStyle w:val="Normal"/><w:spacing w:before="480" w:after="0" w:line="276" w:lineRule="auto"/></w:pPr></w:p></w:tc></w:tr></w:tbl>`
}

function cellXml(
  text: string,
  opts: {
    bold?: boolean
    color?: string
    align?: 'left' | 'right'
    width: number
    fill?: string
    size?: number
  }
): string {
  const pPr = opts.align
    ? `<w:pPr><w:pStyle w:val="Normal"/><w:jc w:val="${opts.align}"/><w:spacing w:after="0" w:before="0" w:line="240" w:lineRule="auto"/></w:pPr>`
    : `<w:pPr><w:pStyle w:val="Normal"/><w:spacing w:after="0" w:before="0" w:line="240" w:lineRule="auto"/></w:pPr>`
  const shd = opts.fill
    ? `<w:shd w:val="clear" w:color="auto" w:fill="${opts.fill}"/>`
    : ''
  return `<w:tc><w:tcPr><w:tcW w:w="${opts.width}" w:type="dxa"/>${shd}<w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="0" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tcMar></w:tcPr><w:p>${pPr}<w:r>${runProperties({
    bold: opts.bold,
    size: opts.size,
    color: opts.color,
  })}<w:t xml:space="preserve">${xmlEscape(text)}</w:t></w:r></w:p></w:tc>`
}

function tableXml(columnWidths: number[], rows: string, borders: string): string {
  const grid = columnWidths
    .map((width) => `<w:gridCol w:w="${width}"/>`)
    .join('')
  const total = columnWidths.reduce((sum, width) => sum + width, 0)
  return `<w:tbl><w:tblPr><w:tblStyle w:val="TableNormal"/><w:tblW w:w="${total}" w:type="dxa"/><w:tblLayout w:type="fixed"/><w:tblBorders>${borders}</w:tblBorders><w:tblCellMar><w:top w:w="0" w:type="dxa"/><w:left w:w="0" w:type="dxa"/><w:bottom w:w="0" w:type="dxa"/><w:right w:w="0" w:type="dxa"/></w:tblCellMar></w:tblPr><w:tblGrid>${grid}</w:tblGrid>${rows}</w:tbl>`
}

function particularsTable(rows: { label: string; value: string }[]): string {
  const labelWidth = 2800
  const valueWidth = CONTENT_WIDTH - labelWidth
  const rowXml = rows
    .map(
      (row) =>
        `<w:tr>${cellXml(row.label, {
          width: labelWidth,
          color: MUTED,
          size: 20,
        })}${cellXml(row.value, {
          width: valueWidth,
          bold: true,
          size: 20,
        })}</w:tr>`
    )
    .join('')

  const borders = [
    lineBorder('top', INK),
    lineBorder('bottom', INK),
    noneBorder('left'),
    noneBorder('right'),
    lineBorder('insideH', RULE, 4),
    noneBorder('insideV'),
  ].join('')

  return tableXml([labelWidth, valueWidth], rowXml, borders)
}

function donationsTable(letter: ClaimLetterContent): string {
  const widths = [1600, CONTENT_WIDTH - 3200, 1600]
  const header = [letter.tableDate, letter.tableCharity, letter.tableAmount]
  const dataRows = letter.donations.map((row) => [
    row.date,
    row.charity,
    row.amount,
  ])
  const totalRow = ['', letter.tableTotal, letter.amountGiven]

  const headerXml = `<w:tr><w:trPr><w:tblHeader/></w:trPr>${header
    .map((cell, index) =>
      cellXml(cell, {
        width: widths[index]!,
        bold: true,
        size: 18,
        fill: HEADER_FILL,
        align: index === 2 ? 'right' : 'left',
      })
    )
    .join('')}</w:tr>`

  const bodyXml = dataRows
    .map(
      (cells) =>
        `<w:tr>${cells
          .map((cell, index) =>
            cellXml(cell, {
              width: widths[index]!,
              size: 18,
              align: index === 2 ? 'right' : 'left',
            })
          )
          .join('')}</w:tr>`
    )
    .join('')

  const totalXml = `<w:tr>${totalRow
    .map((cell, index) =>
      cellXml(cell, {
        width: widths[index]!,
        bold: true,
        size: 18,
        align: index === 0 ? 'left' : 'right',
      })
    )
    .join('')}</w:tr>`

  const borders = [
    lineBorder('top', INK),
    lineBorder('bottom', INK),
    noneBorder('left'),
    noneBorder('right'),
    noneBorder('insideH'),
    noneBorder('insideV'),
  ].join('')

  return tableXml(widths, `${headerXml}${bodyXml}${totalXml}`, borders)
}

function pageBreak(): string {
  return '<w:p><w:pPr><w:pStyle w:val="Normal"/><w:spacing w:after="0" w:before="0" w:line="276" w:lineRule="auto"/></w:pPr><w:r><w:br w:type="page"/></w:r></w:p>'
}

function scheduleIdentityRows(letter: ClaimLetterContent) {
  return [
    { label: 'Name', value: letter.senderName || '____________________' },
    ...letter.particulars.filter(
      (row) =>
        row.label === 'National Insurance number' || row.label === 'Tax year'
    ),
    { label: 'Total Gift Aided donations', value: letter.amountGiven },
  ]
}

function schedulePage(letter: ClaimLetterContent): string[] {
  return [
    pageBreak(),
    paragraph(letter.scheduleEyebrow, {
      bold: true,
      after: 40,
      size: 18,
      color: MUTED,
    }),
    paragraph(letter.scheduleFooter, { after: 160, size: 18, color: MUTED }),
    paragraph(letter.scheduleHeading, { bold: true, after: 40, size: 32 }),
    paragraph(letter.scheduleSubheading, { after: 160, size: 20, color: MUTED }),
    paragraph(letter.scheduleIntro, { after: 240 }),
    particularsTable(scheduleIdentityRows(letter)),
    paragraph(letter.scheduleCount, {
      before: 200,
      after: 160,
      size: 19,
      color: MUTED,
    }),
    donationsTable(letter),
    paragraph('', { after: 0 }),
  ]
}

/** WordprocessingML document body. Exported so tests can check the letter text. */
export function claimLetterDocumentXml(letter: ClaimLetterContent): string {
  const senderName = letter.senderName || '____________________'
  const sender = [
    paragraph(senderName, {
      bold: true,
      after: 0,
      align: 'right',
      size: 22,
    }),
    ...letter.senderAddressLines.map((line) =>
      paragraph(line, {
        after: 0,
        align: 'right',
        size: 20,
      })
    ),
    paragraph(letter.letterDate, {
      before: 200,
      after: 360,
      align: 'right',
      size: 20,
    }),
  ]

  const parts = [
    ...sender,
    paragraph(letter.greeting, { after: 240 }),
    paragraph(letter.subject, { bold: true, after: 240 }),
    paragraph(letter.opening, { after: 200 }),
    paragraph(letter.totalGiven, { after: 200 }),
    paragraph(letter.rateClaim, { after: 280 }),
    paragraph(letter.particularsHeading, { bold: true, after: 80 }),
    particularsTable(letter.particulars),
  ]

  if (letter.enclosureNote) {
    parts.push(paragraph(letter.enclosureNote, { before: 160, after: 240 }))
  }

  parts.push(
    paragraph(letter.giftAidCover, { after: 200 }),
    paragraph(letter.request, { after: 360 }),
    paragraph(letter.closing, { after: 80 }),
    signatureLine(),
    paragraph(senderName, { bold: true, after: 200, before: 80 })
  )

  if (letter.preparedNote) {
    parts.push(
      paragraph(letter.preparedNote, {
        before: 360,
        italics: true,
        size: 18,
        color: MUTED,
      })
    )
  }

  if (letter.includeSchedule) {
    parts.push(...schedulePage(letter))
  }

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    ${parts.join('')}
    <w:sectPr>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="1280" w:right="1440" w:bottom="1280" w:left="1440" w:header="720" w:footer="720" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`
}

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults>
    <w:rPrDefault>
      <w:rPr>
        ${rFonts()}
        <w:sz w:val="22"/>
        <w:szCs w:val="22"/>
        <w:color w:val="${INK}"/>
        <w:lang w:val="en-GB"/>
      </w:rPr>
    </w:rPrDefault>
    <w:pPrDefault>
      <w:pPr>
        <w:spacing w:after="200" w:line="276" w:lineRule="auto"/>
      </w:pPr>
    </w:pPrDefault>
  </w:docDefaults>
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:qFormat/>
    <w:pPr>
      <w:spacing w:after="200" w:line="276" w:lineRule="auto"/>
    </w:pPr>
    <w:rPr>
      ${rFonts()}
      <w:sz w:val="22"/>
      <w:szCs w:val="22"/>
      <w:color w:val="${INK}"/>
      <w:lang w:val="en-GB"/>
    </w:rPr>
  </w:style>
  <w:style w:type="table" w:default="1" w:styleId="TableNormal">
    <w:name w:val="Table Normal"/>
    <w:uiPriority w:val="99"/>
    <w:semiHidden/>
    <w:unhideWhenUsed/>
    <w:tblPr>
      <w:tblCellMar>
        <w:top w:w="0" w:type="dxa"/>
        <w:left w:w="0" w:type="dxa"/>
        <w:bottom w:w="0" w:type="dxa"/>
        <w:right w:w="0" w:type="dxa"/>
      </w:tblCellMar>
    </w:tblPr>
  </w:style>
</w:styles>`

const SETTINGS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:zoom w:percent="100"/>
  <w:defaultTabStop w:val="720"/>
  <w:characterSpacingControl w:val="doNotCompress"/>
  <w:compat>
    <w:compatSetting w:name="compatibilityMode" w:uri="http://schemas.microsoft.com/office/word" w:val="15"/>
  </w:compat>
</w:settings>`

const FONT_TABLE_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:fonts xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:font w:name="${FONT}">
    <w:charset w:val="00"/>
    <w:family w:val="roman"/>
    <w:pitch w:val="variable"/>
  </w:font>
</w:fonts>`

const CONTENT_TYPES_BASE = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>
  <Override PartName="/word/fontTable.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.fontTable+xml"/>
</Types>`

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`

const DOCUMENT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rIdStyles" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rIdSettings" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>
  <Relationship Id="rIdFonts" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/fontTable" Target="fontTable.xml"/>
</Relationships>`

function utf8(text: string): Uint8Array {
  return new TextEncoder().encode(text)
}

export function buildClaimLetterDocxBytes(
  letter: ClaimLetterContent
): Uint8Array {
  return buildStoredZip([
    { path: '[Content_Types].xml', data: utf8(CONTENT_TYPES_BASE) },
    { path: '_rels/.rels', data: utf8(ROOT_RELS) },
    {
      path: 'word/_rels/document.xml.rels',
      data: utf8(DOCUMENT_RELS),
    },
    { path: 'word/styles.xml', data: utf8(STYLES_XML) },
    { path: 'word/settings.xml', data: utf8(SETTINGS_XML) },
    { path: 'word/fontTable.xml', data: utf8(FONT_TABLE_XML) },
    { path: 'word/document.xml', data: utf8(claimLetterDocumentXml(letter)) },
  ])
}

export function downloadClaimLetterDocx(letter: ClaimLetterContent): void {
  const bytes = buildClaimLetterDocxBytes(letter)
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })
  downloadBlob(blob, claimLetterFilename(letter.yearLabel, 'docx'))
}
